import { MRT_ColumnDef } from "material-react-table";
import { Control, Path, UseFieldArrayRemove } from "react-hook-form";

import { PRICE_DIMENSION, PRICE_DIMENSION_LABEL, TPriceDimension } from "@/features/organization/product/constants/productEnums";
import { DeleteCell, MoneyCell, SelectCell, TextCell } from "@/features/organization/product/components/product-form/formTableCells";
import { ComboUnitPriceCell, SubtotalCell } from "@/features/organization/product/components/product-form/product-combination/combinationCells";
import { TOption } from "@/types/common";
import { TPriceCombinationForm, TProductForm, TTypePriceForm } from "@/api/services/main/organization/product/dtos/product.dto";

type TEnabledDimensions = {
  nationality: boolean;
  field: boolean;
  tier: boolean;
};

type TBuildCombinationColumnsParams = {
  control: Control<TProductForm>;
  typePrices: TTypePriceForm[];
  nationalityOptions: TOption[];
  fieldOptions: TOption[];
  thirdDimensionOptions: TOption[];
  showThirdDimension: boolean;
  thirdDimensionHeader: string;
  thirdDimension: TPriceDimension;
  enabledDimensions: TEnabledDimensions;
  remove: UseFieldArrayRemove;
};

const getCombinationFieldPath = (
  rowIndex: number,
  fieldKey: keyof TPriceCombinationForm,
): Path<TProductForm> => `combinations.${rowIndex}.${fieldKey}` as Path<TProductForm>;

export const buildCombinationColumns = ({
  control,
  typePrices,
  nationalityOptions,
  fieldOptions,
  thirdDimensionOptions,
  showThirdDimension,
  thirdDimensionHeader,
  thirdDimension,
  enabledDimensions,
  remove,
}: TBuildCombinationColumnsParams): MRT_ColumnDef<TPriceCombinationForm>[] => {
  const columns: MRT_ColumnDef<TPriceCombinationForm>[] = [
    {
      id: "nationality",
      header: PRICE_DIMENSION_LABEL[PRICE_DIMENSION.NATIONALITY],
      size: 280,
      Cell: ({ row }) => (
        <SelectCell
          control={control}
          name={getCombinationFieldPath(row.index, "nationalityId")}
          options={nationalityOptions}
          disabled={!enabledDimensions.nationality}
          clearable
        />
      ),
    },
    {
      id: "field",
      header: PRICE_DIMENSION_LABEL[PRICE_DIMENSION.FIELD],
      size: 185,
      Cell: ({ row }) => (
        <SelectCell
          control={control}
          name={getCombinationFieldPath(row.index, "fieldId")}
          options={fieldOptions}
          disabled={!enabledDimensions.field}
          clearable
        />
      ),
    },
  ];

  if (showThirdDimension) {
    columns.push({
      id: "third",
      header: thirdDimensionHeader,
      size: 200,
      Cell: ({ row }) => (
        <SelectCell
          control={control}
          name={getCombinationFieldPath(row.index, "variantTypeId")}
          options={thirdDimensionOptions}
          disabled={!enabledDimensions.tier}
          clearable
        />
      ),
    });
  }

  columns.push(
    {
      id: "unitPrice",
      header: "単価/名",
      size: 150,
      muiTableBodyCellProps: { align: "right" },
      Cell: ({ row }) => (
        <ComboUnitPriceCell
          control={control}
          index={row.index}
          typePrices={typePrices}
          enabled={enabledDimensions}
          thirdDim={thirdDimension}
        />
      ),
    },
    {
      id: "discount",
      header: "特定技能2号値引き",
      size: 170,
      Cell: ({ row }) => (
        <MoneyCell control={control} name={getCombinationFieldPath(row.index, "discountTokutei2")} />
      ),
    },
    {
      id: "subtotal",
      header: "小計",
      size: 120,
      muiTableBodyCellProps: { align: "right" },
      Cell: ({ row }) => (
        <SubtotalCell
          control={control}
          index={row.index}
          typePrices={typePrices}
          enabled={enabledDimensions}
          thirdDim={thirdDimension}
        />
      ),
    },
    {
      id: "memo",
      header: "メモ",
      size: 200,
      Cell: ({ row }) => <TextCell control={control} name={getCombinationFieldPath(row.index, "memo")} />,
    },
    {
      id: "action",
      header: "",
      size: 52,
      Cell: ({ row }) => <DeleteCell onClick={() => remove(row.index)} />,
    },
  );

  return columns;
};
